import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Home, Search, Target, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BottomNavbar = ({ sidebarOpen = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const getInitials = (name = "User") => {
    const words = name.trim().split(/\s+/);
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  };

  const navItems = [
    {
      id: "home",
      navRoute: "/home",
      icon: Home,
      label: "Home",
    },
    {
      id: "explore",
      navRoute: "/explore",
      icon: Search,
      label: "Explore",
    },
    {
      id: "create",
      label: "Create",
      navRoute: "/create-post",
      isCenter: true,
    },
    {
      id: "for-you",
      icon: Target,
      navRoute: "/feed?type=for_you",
      label: "For You",
    },
    {
      id: "profile",
      icon: User,
      label: "Profile",
      isProfile: true,
      navRoute: `/${user?.profile?.username}`,
    },
  ];

  // Logic to determine which tab is active based on current URL
  const checkIsActive = (item) => {
    const currentPath = location.pathname;
    const currentSearch = location.search;

    if (item.id === "home") return currentPath === "/home";
    if (item.id === "for-you")
      return currentPath === "/feed" && currentSearch === "?type=for_you";
    if (item.id === "explore")
      return currentPath === "/explore" && currentSearch !== "?type=for_you";
    if (item.id === "profile") return currentPath === item.navRoute;
    return false;
  };

  const handleNavClick = (route) => {
    navigate(route);
  };

  // Hide bottom navbar when sidebar is open
  if (sidebarOpen) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-white border-t border-gray-200">
      <div className="flex justify-around items-end px-4 py-2 max-w-full">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = checkIsActive(item);

          if (item.isCenter) {
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.navRoute)}
                className="w-14 h-14 p-2 bg-[#b0d284] text-black rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all flex items-center justify-center"
              >
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="black"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="10" y1="0" x2="10" y2="20" />
                  <line x1="0" y1="10" x2="20" y2="10" />
                </svg>
              </button>
            );
          }

          if (item.isProfile) {
            const profilePic = user?.profile?.profilePicture;
            const profilePictureUrl =
              profilePic && typeof profilePic === "object"
                ? profilePic.small || profilePic.medium || profilePic.thumbnail
                : profilePic;

            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.navRoute)}
                className="flex flex-col items-center gap-1 transition-all"
              >
                {/* Active black border/ring applied to this wrapper div */}
                <div
                  className={`w-7 h-7 rounded-full overflow-hidden border-2 transition-all flex-shrink-0 ${
                    isActive
                      ? "border-gray-800 ring-gray-300"
                      : "border-gray-400"
                  }`}
                >
                  {profilePictureUrl ? (
                    <img
                      src={profilePictureUrl}
                      className="w-full h-full object-cover"
                      alt="Profile"
                    />
                  ) : user?.profile?.name ? (
                    <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                      <span className="text-slate-600 font-bold text-[10px]">
                        {getInitials(user?.profile?.name)}
                      </span>
                    </div>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                      <svg
                        className="w-full h-full text-slate-400"
                        viewBox="0 0 100 100"
                        fill="currentColor"
                      >
                        <circle cx="50" cy="35" r="18" />
                        <path d="M50 58 C 25 58, 10 65, 10 100 L 90 100 C 90 65, 75 58, 50 58 Z" />
                      </svg>
                    </div>
                  )}
                </div>
                <span
                  className={`text-xs font-medium ${
                    isActive ? "text-gray-800" : "text-gray-500"
                  }`}
                >
                  {item.label}
                </span>
              </button>
            );
          }

          return (
            <button
              key={item.id}
              onClick={() => handleNavClick(item.navRoute)}
              className="flex flex-col items-center gap-1 transition-all"
              aria-label={item.label}
            >
              <Icon
                size={24}
                className={`transition-colors ${
                  isActive
                    ? "text-gray-800 stroke-[2]"
                    : "text-gray-400 stroke-[1.5]"
                }`}
                strokeWidth={isActive ? 2 : 1.5}
              />
              <span
                className={`text-xs font-medium transition-colors ${
                  isActive ? "text-gray-800" : "text-gray-500"
                }`}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BottomNavbar;